import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import '@/lib/leaflet-global';
import { MapContainer, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { VectorTileLayer } from '@/components/crisis/VectorTileLayer';
import { Clock, MapPin, AlertCircle, Navigation } from 'lucide-react';
import { getColorHex } from './CarIconSelector';
import CAR_ICON_DATA from './carIconData';

// Generate Leaflet icon from car icon data
const createDriverIcon = (iconId, colorName = 'pink', heading = 0) => {
  const colorHex = getColorHex(colorName);
  let svg;
  const iconData = CAR_ICON_DATA[iconId];

  if (!iconData || iconId === 'arrow') {
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="18" fill="rgba(0,0,0,0.25)"/>
      <circle cx="24" cy="24" r="15" fill="#1a1a2e" stroke="${colorHex}" stroke-width="1.5"/>
      <g transform="rotate(${heading}, 24, 24)">
        <path d="M24 10 L32 30 L24 25 L16 30 Z" fill="${colorHex}"/>
      </g>
    </svg>`;
  } else {
    const imgW = 36, imgH = Math.round(imgW * iconData.height / iconData.width);
    const imgX = (48 - imgW) / 2, imgY = (48 - imgH) / 2;
    svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="48" height="48" viewBox="0 0 48 48">
      <defs><clipPath id="cc"><circle cx="24" cy="24" r="17"/></clipPath></defs>
      <circle cx="24" cy="24" r="22" fill="rgba(0,0,0,0.3)"/>
      <circle cx="24" cy="24" r="19" fill="#1a1a2e" stroke="${colorHex}" stroke-width="1.5"/>
      <image href="${iconData.src}" x="${imgX}" y="${imgY}" width="${imgW}" height="${imgH}" clip-path="url(#cc)" preserveAspectRatio="xMidYMid meet"/>
    </svg>`;
  }

  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [48, 48],
    iconAnchor: [24, 24],
  });
};

// Map auto-fitter
const MapFitter = ({ driverLocation, pickupLocation, destinationLocation, trackingStatus }) => {
  const map = useMap();
  const hasFit = useRef(false);

  useEffect(() => {
    if (hasFit.current) return;
    const dest = trackingStatus === 'passenger_collected' ? destinationLocation : pickupLocation;
    const points = [driverLocation, dest].filter(p => p && Number.isFinite(p.lat));
    if (points.length >= 2) {
      const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
      hasFit.current = true;
    } else if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
      hasFit.current = true;
    }
  }, [map, driverLocation, pickupLocation, destinationLocation, trackingStatus]);

  return null;
};

// Driver marker (Leaflet)
const DriverMarker = ({ position, carIcon, carColor, heading }) => {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (!position || !Number.isFinite(position.lat)) return;
    const icon = createDriverIcon(carIcon, carColor, heading || 0);

    if (markerRef.current) {
      markerRef.current.setLatLng([position.lat, position.lng]);
      markerRef.current.setIcon(icon);
    } else {
      markerRef.current = L.marker([position.lat, position.lng], { icon, zIndexOffset: 2000 }).addTo(map);
    }

    return () => {};
  }, [map, position, carIcon, carColor, heading]);

  useEffect(() => {
    return () => {
      if (markerRef.current) { try { map.removeLayer(markerRef.current); } catch {} }
    };
  }, [map]);

  return null;
};

// Pickup/destination pin marker
const PinMarker = ({ position, type }) => {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (!position || !Number.isFinite(position.lat)) return;
    const color = type === 'pickup' ? '#22c55e' : '#a855f7';
    const icon = L.divIcon({
      html: `<div style="width:20px;height:20px;background:${color};border:3px solid #fff;border-radius:50%;box-shadow:0 0 12px ${color}80"></div>`,
      className: '',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    if (markerRef.current) {
      markerRef.current.setLatLng([position.lat, position.lng]);
      markerRef.current.setIcon(icon);
    } else {
      markerRef.current = L.marker([position.lat, position.lng], { icon, zIndexOffset: 1500 }).addTo(map);
    }
  }, [map, position, type]);

  useEffect(() => {
    return () => {
      if (markerRef.current) { try { map.removeLayer(markerRef.current); } catch {} }
    };
  }, [map]);

  return null;
};

const LiveTrackingMapLeaflet = ({
  driverLocation,
  pickupLocation,
  destinationLocation,
  eta,
  trackingStatus,
  carColor = 'pink',
  carIcon = 'arrow',
  locationTimestamp
}) => {
  const [isStale, setIsStale] = useState(false);
  const [routePath, setRoutePath] = useState([]);

  // Check if location is stale
  useEffect(() => {
    if (!locationTimestamp) return;
    const checkStale = () => {
      const diff = (Date.now() - new Date(locationTimestamp).getTime()) / 1000;
      setIsStale(diff > 120);
    };
    checkStale();
    const interval = setInterval(checkStale, 10000);
    return () => clearInterval(interval);
  }, [locationTimestamp]);

  const getTimeAgo = (ts) => {
    if (!ts) return null;
    const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (s < 60) return 'Just now';
    if (s < 120) return '1 min ago';
    if (s < 3600) return `${Math.floor(s / 60)} mins ago`;
    return `${Math.floor(s / 3600)}h ago`;
  };

  // Calculate route using OSRM
  useEffect(() => {
    const dest = trackingStatus === 'passenger_collected' ? destinationLocation : pickupLocation;
    if (!driverLocation || !dest) { setRoutePath([]); return; }

    const fetchRoute = async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${driverLocation.lng},${driverLocation.lat};${dest.lng},${dest.lat}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.routes?.[0]?.geometry?.coordinates) {
          setRoutePath(data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]));
        }
      } catch {
        // Draw straight line as fallback
        setRoutePath([[driverLocation.lat, driverLocation.lng], [dest.lat, dest.lng]]);
      }
    };
    fetchRoute();
  }, [driverLocation, pickupLocation, destinationLocation, trackingStatus]);

  const center = driverLocation
    ? [driverLocation.lat, driverLocation.lng]
    : pickupLocation
      ? [pickupLocation.lat, pickupLocation.lng]
      : [51.5074, -0.1278];

  const destination = trackingStatus === 'passenger_collected' ? destinationLocation : pickupLocation;

  return (
    <div className="space-y-2" data-testid="live-tracking-map-leaflet">
      {/* ETA Bar */}
      {eta?.eta_available && (
        <div className="flex items-center justify-between bg-card/80 backdrop-blur rounded-xl px-4 py-2.5 border border-border/50">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">
              {eta.destination_type === 'pickup' ? 'Arriving in' : 'ETA to destination'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-base font-bold text-primary">{eta.duration_text}</span>
            {eta.distance_text && (
              <span className="text-xs text-muted-foreground">{eta.distance_text}</span>
            )}
          </div>
        </div>
      )}

      {/* Stale warning */}
      {isStale && (
        <div className="flex items-center gap-2 bg-yellow-500/10 text-yellow-500 px-3 py-2 rounded-lg text-xs">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Location update: {getTimeAgo(locationTimestamp)}</span>
        </div>
      )}

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-border/30" style={{ height: '300px' }}>
        <MapContainer
          center={center}
          zoom={14}
          maxZoom={19}
          minZoom={3}
          style={{ width: '100%', height: '100%', background: '#f2efe9' }}
          zoomControl={false}
          attributionControl={false}
        >
          <VectorTileLayer />
          <MapFitter
            driverLocation={driverLocation}
            pickupLocation={pickupLocation}
            destinationLocation={destinationLocation}
            trackingStatus={trackingStatus}
          />
          {driverLocation && (
            <DriverMarker
              position={driverLocation}
              carIcon={carIcon}
              carColor={carColor}
              heading={driverLocation.heading || 0}
            />
          )}
          {pickupLocation && <PinMarker position={pickupLocation} type="pickup" />}
          {destination && destination !== pickupLocation && (
            <PinMarker position={destination} type="destination" />
          )}
          {routePath.length >= 2 && (
            <Polyline
              positions={routePath}
              pathOptions={{ color: '#3b82f6', weight: 5, opacity: 0.8 }}
            />
          )}
        </MapContainer>
      </div>

      {/* Status footer */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Navigation className="w-3 h-3" />
        <span>
          {trackingStatus === 'en_route_to_pickup' ? 'Driver en route to pickup' :
           trackingStatus === 'passenger_collected' ? 'Heading to destination' :
           'Tracking active'}
        </span>
        {!isStale && locationTimestamp && (
          <span className="text-primary/70">Updated {getTimeAgo(locationTimestamp)}</span>
        )}
      </div>
    </div>
  );
};

export default LiveTrackingMapLeaflet;
